/**
 * Token Manager for V2 Authentication System
 * Handles storage and retrieval of access token and user data
 * Scalable for role and permission system
 */

const TOKEN_KEY = "accessTokenV2"
const REFRESH_TOKEN_KEY = "refreshTokenV2"
const USER_DATA_KEY = "userDataV2"
const SESSION_ID_KEY = "sessionIdV2"

/**
 * Store access token
 * @param {string} token - Access token
 */
export const setAccessToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  }
}

/**
 * Get access token
 * @returns {string|null} Access token or null
 */
export const getAccessToken = () => {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * Remove access token
 */
export const removeAccessToken = () => {
  localStorage.removeItem(TOKEN_KEY)
}

/**
 * Store refresh token
 * @param {string} token - Refresh token
 */
export const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token)
  }
}

/**
 * Get refresh token
 * @returns {string|null} Refresh token or null
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

/**
 * Remove refresh token
 */
export const removeRefreshToken = () => {
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/**
 * Store user data
 * @param {Object} userData - User detail object
 */
export const setUserData = (userData) => {
  if (userData) {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData))
  }
}

/**
 * Get user data
 * @returns {Object|null} User data object or null
 */
export const getUserData = () => {
  const userData = localStorage.getItem(USER_DATA_KEY)
  return userData ? JSON.parse(userData) : null
}

/**
 * Remove user data
 */
export const removeUserData = () => {
  localStorage.removeItem(USER_DATA_KEY)
}

/**
 * Store session ID (for 2FA)
 * @param {string} sessionId - Session ID
 */
export const setSessionId = (sessionId) => {
  if (sessionId) {
    localStorage.setItem(SESSION_ID_KEY, sessionId)
  }
}

/**
 * Get session ID
 * @returns {string|null} Session ID or null
 */
export const getSessionId = () => {
  return localStorage.getItem(SESSION_ID_KEY)
}

/**
 * Remove session ID
 */
export const removeSessionId = () => {
  localStorage.removeItem(SESSION_ID_KEY)
}

/**
 * Check if user is logged in
 * @returns {boolean} True if access token exists
 */
export const isUserLoggedInV2 = () => {
  return !!getAccessToken()
}

/**
 * Get user role (for role-based access control)
 * @returns {string|null} User role or null
 */
export const getUserRole = () => {
  const userData = getUserData()
  return userData?.role || null
}

/**
 * Get user permissions/abilities (for permission system)
 * @returns {Array} Array of abilities/permissions
 */
export const getUserAbilities = () => {
  const userData = getUserData()
  return userData?.ability || []
}

/**
 * Get user access structure (for navigation/permissions)
 * @returns {Array} Array of access objects
 */
export const getUserAccess = () => {
  const userData = getUserData()
  return userData?.access || []
}

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  removeAccessToken()
  removeRefreshToken()
  removeUserData()
  removeSessionId()
}

/**
 * Initialize auth data from login response
 * @param {Object} responseData - Login API response data
 * @param {string} responseData.access_token - Access token
 * @param {string} responseData.refresh_token - Refresh token
 * @param {Object} responseData.user_detail - User detail object
 */
export const initializeAuthData = (responseData) => {
  if (responseData?.access_token) {
    setAccessToken(responseData.access_token)
  }
  if (responseData?.refresh_token) {
    setRefreshToken(responseData.refresh_token)
  }
  if (responseData?.user_detail) {
    setUserData(responseData.user_detail)
  }
}

/**
 * Update tokens from refresh API response (keeps existing user_detail)
 * @param {Object} responseData - Refresh API response data
 * @param {string} responseData.access_token - New access token
 * @param {string} responseData.refresh_token - New refresh token
 */
export const updateTokensFromRefresh = (responseData) => {
  if (responseData?.access_token) {
    setAccessToken(responseData.access_token)
  }
  if (responseData?.refresh_token) {
    setRefreshToken(responseData.refresh_token)
  }
}

/**
 * Check if token is expired (if token is JWT)
 * @returns {boolean} True if token is expired
 */
export const isTokenExpired = () => {
  try {
    const token = getAccessToken()
    if (!token) return true

    // Try to decode JWT if it is one
    const parts = token.split(".")
    if (parts.length !== 3) return false // Not a JWT

    const payload = JSON.parse(atob(parts[1]))
    if (payload.exp) {
      return payload.exp < Date.now() / 1000
    }
    return false
  } catch (error) {
    return false
  }
}

